<script setup>
// NOTICE: KAPTION-INTEGRATION
// sales-pipeline.BOARD.1 — the configurable Sales Pipeline board (A + B):
// header (filters, search, group-by, admin gear), stage columns with WIP limits
// and collapsible Won/Lost strips, optional swimlanes by assignee/team, the
// conversation side drawer, and admin settings — all working together.

import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import { useKaptionSalesPipeline } from 'dashboard/composables/useKaptionSalesPipeline.js';
import {
  GROUP_BY,
  NO_LANE_ID,
  buildLanes,
  dropExceedsWip,
} from 'dashboard/helper/kaptionSalesPipeline.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KaptionSalesKanbanClassic from './KaptionSalesKanbanClassic.vue';
import KaptionSalesKanbanDrawer from './KaptionSalesKanbanDrawer.vue';
import KaptionSalesPipelineColumn from './KaptionSalesPipelineColumn.vue';
import KaptionSalesPipelineHeader from './KaptionSalesPipelineHeader.vue';
import KaptionSalesPipelineSettings from './KaptionSalesPipelineSettings.vue';
import KaptionSalesPipelineSwimlanes from './KaptionSalesPipelineSwimlanes.vue';

const props = defineProps({
  isDarkMode: { type: Boolean, default: false },
  // sales-pipeline.HEADER.2 — the chat list header's selection (see ChatList).
  listScope: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { canUseFeature } = useFeaturePermissions();

const {
  canManage: serverCanManage,
  configLoaded,
  configError,
  pipeline,
  stages,
  scope,
  filters,
  search,
  groupBy,
  column,
  visibleItems,
  allVisible,
  loadConfig,
  fetchAll,
  loadMore,
  moveCard,
  reassignCard,
  applyLiveUpdate,
  saveConfig,
  createDefault,
} = useKaptionSalesPipeline({ listScope: toRef(props, 'listScope') });

// sales-pipeline.CARD.2 — the header's layout toggle (SwitchLayout) drives the
// card density; condensed is the default, like the conversation list.
const { uiSettings } = useUISettings();
const condensed = computed(
  () =>
    (uiSettings.value?.conversation_display_type ||
      wootConstants.LAYOUT_TYPES.CONDENSED) ===
    wootConstants.LAYOUT_TYPES.CONDENSED
);

// sales-pipeline.PERMISSION.1 — the gear needs the server's verdict (admin role +
// sales_pipeline_settings) AND the client-side checks; the PUT re-checks anyway.
const canManage = computed(
  () =>
    serverCanManage.value &&
    isAdmin.value &&
    canUseFeature('sales_pipeline_settings')
);
const canReassign = computed(() => canUseFeature('direct_assignment'));

const showSettings = ref(false);
const saving = ref(false);
const saveError = ref('');

// ── Collapsible terminal columns & lanes ────────────────────────────────────
const collapsedStages = reactive({});
const collapsedLanes = reactive({});
watch(
  stages,
  list => {
    list.forEach(stage => {
      if (stage.terminal && !(stage.id in collapsedStages))
        collapsedStages[stage.id] = true;
    });
  },
  { immediate: true }
);
const toggleStage = id => {
  collapsedStages[id] = !collapsedStages[id];
};
const toggleLane = id => {
  collapsedLanes[id] = !collapsedLanes[id];
};

// ── Lane people ────────────────────────────────────────────────────────────
const within = (list, ids) =>
  ids?.length ? list.filter(item => ids.includes(item.id)) : list;
const teamOptions = computed(() =>
  within(store.getters['teams/getTeams'] || [], scope.value.team_ids).map(
    team => ({ id: team.id, name: team.name })
  )
);
const agents = computed(() => store.getters['agents/getAgents'] || []);

// ── Board data ─────────────────────────────────────────────────────────────
const itemsByStage = computed(() => {
  const map = {};
  stages.value.forEach(stage => {
    map[stage.id] = visibleItems(stage.id);
  });
  return map;
});
const totals = computed(() => {
  const map = {};
  stages.value.forEach(stage => {
    map[stage.id] = column(stage.id).total;
  });
  return map;
});
const hasMore = computed(() => {
  const map = {};
  stages.value.forEach(stage => {
    map[stage.id] = column(stage.id).hasMore && column(stage.id).page > 0;
  });
  return map;
});

const lanes = computed(() => {
  if (groupBy.value === GROUP_BY.NONE) return [];
  const isTeam = groupBy.value === GROUP_BY.TEAM;
  const people = isTeam
    ? teamOptions.value
    : agents.value.map(a => ({
        id: a.id,
        name: a.available_name || a.name,
        thumbnail: a.thumbnail,
      }));
  return buildLanes({
    groupBy: groupBy.value,
    conversations: allVisible.value,
    people,
    noneName: isTeam
      ? t('KAPTION_PLATFORM.SALES_PIPELINE.LANES.NO_TEAM')
      : t('KAPTION_PLATFORM.SALES_PIPELINE.LANES.UNASSIGNED'),
  });
});

// ── Clock for waiting times ────────────────────────────────────────────────
const nowSeconds = ref(Math.floor(Date.now() / 1000));
let clock = null;

// ── Drag & drop ────────────────────────────────────────────────────────────
const drag = reactive({
  active: false,
  fromStageId: null,
  fromLaneId: null,
  stageId: null,
  laneId: null,
  exceeds: false,
});

function onDragStart(evt) {
  drag.active = true;
  drag.fromStageId = evt.from?.dataset?.stageId || null;
  drag.fromLaneId = evt.from?.dataset?.laneId || null;
}

function onDragEnd() {
  Object.assign(drag, {
    active: false,
    fromStageId: null,
    fromLaneId: null,
    stageId: null,
    laneId: null,
    exceeds: false,
  });
}

// sales-pipeline.DRAG.2 — highlight the target; dashed red when the drop would
// exceed the WIP limit; refuse cross-lane drops without direct_assignment.
function moveGuard(evt) {
  const toStageId = evt.to?.dataset?.stageId || null;
  const toLaneId = evt.to?.dataset?.laneId || null;
  const stage = stages.value.find(s => s.id === toStageId);
  drag.stageId = toStageId;
  drag.laneId = toLaneId;
  drag.exceeds = dropExceedsWip(
    stage,
    column(toStageId).total,
    drag.fromStageId
  );
  if (
    groupBy.value !== GROUP_BY.NONE &&
    toLaneId &&
    toLaneId !== drag.fromLaneId &&
    !canReassign.value
  )
    return false;
  return true;
}

const laneById = id => lanes.value.find(lane => lane.id === id);

async function onDrop({ conversation, toStageId, toLaneId }) {
  const { fromStageId, fromLaneId } = drag;
  try {
    if (fromStageId && toStageId !== fromStageId) {
      await moveCard(conversation, toStageId);
    }
    if (
      groupBy.value !== GROUP_BY.NONE &&
      toLaneId &&
      toLaneId !== fromLaneId
    ) {
      const lane = laneById(toLaneId);
      await reassignCard(
        conversation,
        toLaneId,
        toLaneId === NO_LANE_ID
          ? null
          : { name: lane?.name, thumbnail: lane?.thumbnail }
      );
    }
  } catch (error) {
    useAlert(t('KAPTION_PLATFORM.SALES_PIPELINE.MOVE_FAILED'));
  }
}

// ── Drawer (same behaviour as the classic board) ───────────────────────────
const drawerConversation = ref(null);
const isDrawerOpen = ref(false);
const drawerIndex = computed(() =>
  allVisible.value.findIndex(c => c.id === drawerConversation.value?.id)
);
const hasPrev = computed(() => drawerIndex.value > 0);
const hasNext = computed(
  () =>
    drawerIndex.value >= 0 && drawerIndex.value < allVisible.value.length - 1
);

async function openCard(conversation) {
  if (!conversation?.id) return;
  try {
    await store.dispatch('getConversation', conversation.id);
  } catch {
    // already in the store
  }
  const stored =
    store.getters.getConversationById(conversation.id) || conversation;
  store.dispatch('setActiveChat', { data: stored });
  drawerConversation.value = conversation;
  isDrawerOpen.value = true;
}

// sales-pipeline.LIVE.2 — closing the drawer re-places only that card (labels
// edited in the sidebar), instead of refetching every column.
function closeDrawer() {
  const current = drawerConversation.value;
  isDrawerOpen.value = false;
  drawerConversation.value = null;
  if (!current) return;
  const stored = store.getters.getConversationById(current.id);
  const labels = store.state.conversationLabels?.records?.[current.id];
  if (!stored && !labels) return;
  // Only board fields: the store copy carries every loaded message (oldest
  // first), which would replace the card's last-message preview.
  const update = {
    id: current.id,
    labels: labels || stored?.labels || current.labels,
  };
  ['status', 'meta', 'waiting_since', 'unread_count', 'inbox_id'].forEach(
    key => {
      if (stored && key in stored) update[key] = stored[key];
    }
  );
  applyLiveUpdate(update);
}

const openPrev = () =>
  hasPrev.value && openCard(allVisible.value[drawerIndex.value - 1]);
const openNext = () =>
  hasNext.value && openCard(allVisible.value[drawerIndex.value + 1]);

// ── Keyboard: scoped to the board element, never global ────────────────────
const boardRef = ref(null);
const focusedId = ref(null);

function onBoardKeydown(event) {
  if (isDrawerOpen.value || showSettings.value) return;
  const tag = event.target?.tagName;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
  const columnsList = stages.value
    .filter(stage => !collapsedStages[stage.id])
    .map(stage => itemsByStage.value[stage.id] || []);
  let col = columnsList.findIndex(list =>
    list.some(c => c.id === focusedId.value)
  );
  let row =
    col >= 0 ? columnsList[col].findIndex(c => c.id === focusedId.value) : -1;
  if (col < 0) {
    col = columnsList.findIndex(list => list.length);
    row = 0;
    if (col < 0) return;
    if (
      ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      event.preventDefault();
      focusedId.value = columnsList[col][0].id;
    }
    return;
  }
  const keys = {
    ArrowDown: () => [col, Math.min(row + 1, columnsList[col].length - 1)],
    ArrowUp: () => [col, Math.max(row - 1, 0)],
    ArrowRight: () => {
      const next = columnsList.findIndex((l, i) => i > col && l.length);
      return next >= 0
        ? [next, Math.min(row, columnsList[next].length - 1)]
        : [col, row];
    },
    ArrowLeft: () => {
      const prev = columnsList
        .map((l, i) => (i < col && l.length ? i : -1))
        .filter(i => i >= 0)
        .pop();
      return prev !== undefined
        ? [prev, Math.min(row, columnsList[prev].length - 1)]
        : [col, row];
    },
  };
  if (keys[event.key]) {
    event.preventDefault();
    const [c, r] = keys[event.key]();
    focusedId.value = columnsList[c][r].id;
  } else if (event.key === 'Enter') {
    event.preventDefault();
    openCard(columnsList[col][row]);
  }
}

// ── Settings ───────────────────────────────────────────────────────────────
async function onSaveSettings(config) {
  saving.value = true;
  saveError.value = '';
  try {
    await saveConfig(config);
    showSettings.value = false;
    fetchAll();
  } catch (error) {
    saveError.value =
      error?.response?.data?.error ||
      t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.SAVE_FAILED');
  } finally {
    saving.value = false;
  }
}

async function onCreateDefault() {
  saving.value = true;
  saveError.value = '';
  try {
    await createDefault({
      pipeline: t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.DEFAULT_NAME'),
      unstaged: t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.UNSTAGED_DEFAULT'),
      'funnel-new-lead': t(
        'KAPTION_PLATFORM.SALES_PIPELINE.DEFAULT_STAGES.NEW_LEAD'
      ),
      'funnel-qualified': t(
        'KAPTION_PLATFORM.SALES_PIPELINE.DEFAULT_STAGES.QUALIFIED'
      ),
      'funnel-proposal': t(
        'KAPTION_PLATFORM.SALES_PIPELINE.DEFAULT_STAGES.PROPOSAL'
      ),
      'funnel-won': t('KAPTION_PLATFORM.SALES_PIPELINE.DEFAULT_STAGES.WON'),
      'funnel-lost': t('KAPTION_PLATFORM.SALES_PIPELINE.DEFAULT_STAGES.LOST'),
    });
    store.dispatch('labels/get');
    fetchAll();
  } catch (error) {
    saveError.value =
      error?.response?.data?.error ||
      t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.SAVE_FAILED');
  } finally {
    saving.value = false;
  }
}

// ── Lifecycle ──────────────────────────────────────────────────────────────
let refetchTimer = null;
// Any change in the header's selection refetches the columns (debounced: a view
// switch changes route props and chips in one tick).
watch(
  () => JSON.stringify(filters.value),
  () => {
    if (!pipeline.value) return;
    clearTimeout(refetchTimer);
    refetchTimer = setTimeout(fetchAll, 150);
  }
);

let unsubscribe = null;
onMounted(async () => {
  clock = setInterval(() => {
    nowSeconds.value = Math.floor(Date.now() / 1000);
  }, 60000);
  // sales-pipeline.LIVE.1 — ActionCable already feeds Vuex; follow it.
  unsubscribe = store.subscribeAction({
    after: action => {
      if (
        action.type === 'updateConversation' ||
        action.type === 'addConversation'
      ) {
        applyLiveUpdate(action.payload);
      }
    },
  });
  await loadConfig();
  if (pipeline.value) fetchAll();
});

onUnmounted(() => {
  clearInterval(clock);
  clearTimeout(refetchTimer);
  unsubscribe?.();
});
</script>

<template>
  <div
    ref="boardRef"
    class="flex flex-col flex-1 min-h-0 overflow-hidden outline-none"
    tabindex="0"
    data-testid="pipeline-board"
    @keydown="onBoardKeydown"
  >
    <div v-if="!configLoaded" class="flex flex-1 items-center justify-center">
      <Spinner class="text-n-brand" />
    </div>

    <!-- sales-pipeline.COMPAT.1 — no pipeline yet: the classic board, unchanged -->
    <template v-else-if="!pipeline && !showSettings">
      <div
        v-if="canManage && !configError"
        class="flex flex-wrap items-center gap-3 mx-2 mt-2 px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1"
        data-testid="pipeline-setup-banner"
      >
        <i class="i-lucide-kanban size-4 text-n-teal-10" />
        <span class="text-sm text-n-slate-12 flex-1 min-w-[12rem]">
          {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETUP.BANNER') }}
        </span>
        <Button
          :label="t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.CREATE_DEFAULT')"
          icon="i-lucide-wand-sparkles"
          color="teal"
          size="sm"
          :is-loading="saving"
          data-testid="pipeline-setup-create-default"
          @click="onCreateDefault"
        />
        <Button
          :label="t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.OPEN')"
          variant="outline"
          color="slate"
          size="sm"
          data-testid="pipeline-setup-configure"
          @click="showSettings = true"
        />
      </div>
      <KaptionSalesKanbanClassic :is-dark-mode="isDarkMode" />
    </template>

    <KaptionSalesPipelineSettings
      v-else-if="showSettings && canManage"
      :pipeline="pipeline"
      :saving="saving"
      :error="saveError"
      @save="onSaveSettings"
      @create-default="onCreateDefault"
      @close="showSettings = false"
    />

    <template v-else>
      <KaptionSalesPipelineHeader
        v-model:search="search"
        v-model:group-by="groupBy"
        :pipeline-name="pipeline.name"
        :unsupported-view="filters.unsupported"
        :show-settings="canManage"
        @open-settings="showSettings = true"
        @refresh="fetchAll"
      />

      <div
        v-if="groupBy === 'none'"
        class="flex flex-1 min-h-0 gap-2 p-2 overflow-x-auto"
        :class="{ 'max-w-[33vw]': isDrawerOpen }"
        data-testid="pipeline-columns"
      >
        <KaptionSalesPipelineColumn
          v-for="stage in stages"
          :key="stage.id"
          :stage="stage"
          :stages="stages"
          :items="itemsByStage[stage.id] || []"
          :total="totals[stage.id] || 0"
          :loading="column(stage.id).loading"
          :has-more="hasMore[stage.id]"
          :collapsed="!!collapsedStages[stage.id]"
          :drag-over="drag.active && drag.stageId === stage.id"
          :drag-exceeds="drag.exceeds"
          :move-guard="moveGuard"
          :now-seconds="nowSeconds"
          :focused-id="focusedId"
          :condensed="condensed"
          @open="openCard"
          @drop="onDrop"
          @load-more="loadMore"
          @toggle-collapse="toggleStage"
          @drag-start="onDragStart"
          @drag-end="onDragEnd"
        />
      </div>

      <KaptionSalesPipelineSwimlanes
        v-else
        :stages="stages"
        :lanes="lanes"
        :group-by="groupBy"
        :items-by-stage="itemsByStage"
        :totals="totals"
        :has-more="hasMore"
        :collapsed-stages="collapsedStages"
        :collapsed-lanes="collapsedLanes"
        :drag-over="drag.active ? drag : {}"
        :move-guard="moveGuard"
        :now-seconds="nowSeconds"
        :focused-id="focusedId"
        :class="{ 'max-w-[33vw]': isDrawerOpen }"
        @open="openCard"
        @drop="onDrop"
        @load-more="loadMore"
        @toggle-collapse="toggleStage"
        @toggle-lane="toggleLane"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
      />
    </template>

    <KaptionSalesKanbanDrawer
      v-if="pipeline"
      :is-open="isDrawerOpen"
      :conversation="drawerConversation"
      :is-dark-mode="isDarkMode"
      :has-prev="hasPrev"
      :has-next="hasNext"
      @close="closeDrawer"
      @prev="openPrev"
      @next="openNext"
    />
  </div>
</template>
